import { useCallback, useState, ChangeEvent } from 'react';
import { uniqueId } from 'lodash';
import { useTranslation } from 'next-i18next';
import { useNotificationsState } from '../../providers/NotificationsState/useNotificationsState';
import { AttachedFileProps } from './utils/upload.types';
import {
  getSignedPostPolicy,
  MAX_FILE_SIZE_BYTES,
  MAX_NUMBER_OF_FILES,
  uploadGoogleAttachment,
} from './utils/upload.utils';

export default function useAttachments() {
  const [uploadedFiles, setUploadedFiles] = useState<AttachedFileProps[]>([]);

  const { t } = useTranslation();

  const { addNotification } = useNotificationsState();

  const addNewFiles = async (fileList: FileList | null) => {
    const attachments: AttachedFileProps[] = Array.from(fileList ?? []).map(
      (file) => ({
        file,
        status: file.size > MAX_FILE_SIZE_BYTES ? 'error' : 'pending',
        id: uniqueId('file_'),
      }),
    );

    const totalLength = attachments.length + uploadedFiles.length;

    if (totalLength > MAX_NUMBER_OF_FILES) {
      addNotification({
        message: t('uploadFailedMaxFiles'),
        type: 'error',
      });
      return;
    }

    setUploadedFiles((prevAttachments) => [...prevAttachments, ...attachments]);

    for (const { file, id, status } of attachments) {
      if (status !== 'pending') {
        continue;
      }
      const signedPostPolicy = await getSignedPostPolicy(file).catch(() => {
        updateFileStatus(0, 'error', id);
      });

      if (!signedPostPolicy) {
        continue;
      }

      const downloadUrl = await uploadGoogleAttachment(
        {
          file,
          signedPostPolicy,
        },
        id,
        updateFileStatus,
      ).catch(() => {
        updateFileStatus(0, 'error', id);
      });

      if (!downloadUrl) {
        continue;
      }

      // set the url for the file
      setUploadedFiles((prevFiles) => {
        const updatedFiles = prevFiles.map((prevFile) => {
          if (prevFile.id === id) {
            return {
              ...prevFile,
              url: downloadUrl,
            };
          }

          return prevFile;
        });

        return updatedFiles;
      });
    }
  };

  const updateFileStatus = useCallback(
    (percentage: number, status: AttachedFileProps['status'], id: string) => {
      setUploadedFiles((prevFiles) =>
        prevFiles.map((file) => {
          if (file.id === id) {
            return {
              ...file,
              status,
              percentage,
            };
          }

          return file;
        }),
      );
    },
    [],
  );

  const onChange = (el: ChangeEvent<HTMLInputElement>) => {
    const { files } = el.target;
    addNewFiles(files);
  };

  const onDrop = (fileList: FileList) => {
    addNewFiles(fileList);
  };

  const onRemove = (id: string) => {
    setUploadedFiles((prevUploadedFiles) =>
      prevUploadedFiles.filter((attachment) => attachment.id !== id),
    );
  };

  return {
    uploadedFiles,
    onChange,
    onDrop,
    onRemove,
  };
}
